import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import { SecurityTypeSelect } from '@dealstack/frontend/shared/components/SecurityTypeSelect/SecurityTypeSelect';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { AlertDescription, Alert } from '@dealstack/frontend/shared/components/Alert/Alert';
import {
	InputWrapper,
	StringInput,
} from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { ButtonsRight } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import {
	CheckedButtonSquare,
	CloseButtonSquare,
	DeleteButtonSquare,
	ThreeDotMenuIconButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Cell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { em } from '@mantine/core';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController';
import { useEffect } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import { useSecurityRowModalController } from './useSecurityRowModalController';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';

interface Props {
	className?: string;
	fieldIndex: number;
	onRemove: () => void;
	showNumericalColumn?: boolean;
	resetEditOnDestroy?: boolean;
	showFairMarketValuePerUnitColumn?: boolean;
}

const SecurityRowModal = ({
	fieldIndex,
	onRemove,
	className,
	showFairMarketValuePerUnitColumn,
	showNumericalColumn,
	resetEditOnDestroy,
}: Props) => {
	// we need to start making re usable hooks and not component specific hooks
	// if we hook is exporting this many props we should be breaking it down into domain logic parts
	// it's fine to have more than one hook, fit for purpose
	const {
		fieldData,
		fieldKeys,
		isValid,
		onSaveButtonClicked,
		onCancelButtonClicked,
		onEditButtonClicked,
		stopEdit,
		disableForVotes,
		disableForLoansSecurityTypes,
		handleBlur,
		showCheckSecurityNameAlert,
		closeAlert,
		onSubmit,
		handleBlurVoterPerShare,
		votesPerUnitValue,
		nominalValue,
		fairMarketValuePerUnit,
	} = useSecurityRowModalController(fieldIndex, onRemove);

	const getSecurityTypeTranslation = useSecurityTypeTranslation();
	const tCommonUi = useTranslationCommonUi();

	useEffect(() => {
		return () => {
			if (!(resetEditOnDestroy && fieldData.isOnEdit)) {
				return;
			}

			stopEdit();
			if (fieldData.isOnAdd) {
				onRemove();
			}
		};
	}, []);

	return (
		<RowWrapper isInvalid={fieldData.isOnEdit && !isValid}>
			<Row className={className}>
				{showNumericalColumn && (
					<Cell>
						<Text variant="p-sm" bold>
							{fieldIndex + 1}.
						</Text>
					</Cell>
				)}

				{fieldData.isOnEdit ? (
					<>
						<UIElementIdentifier identifier={TEST_ELEMENT_IDS.TYPE_OF_SECURITY}>
							<Cell>
								<SecurityTypeSelect name={fieldKeys.type} />
							</Cell>
						</UIElementIdentifier>
						<UIElementIdentifier identifier={TEST_ELEMENT_IDS.NAME_OF_SECURITY}>
							<Cell>
								<StringInput blockRedundantSpaces name={fieldKeys.name} />
							</Cell>
						</UIElementIdentifier>
						<Cell align="right">
							<InputWrapper onBlur={handleBlurVoterPerShare}>
								<StringInput
									blockRedundantSpaces
									name={fieldKeys.votesPerUnit}
									placeholder={
										disableForLoansSecurityTypes || disableForVotes ? '' : '0'
									}
									disabled={disableForLoansSecurityTypes || disableForVotes}
								/>
							</InputWrapper>
						</Cell>
						<Cell align="right">
							<InputWrapper onBlur={() => handleBlur(fieldKeys.nominalValue)}>
								<StringInput
									blockRedundantSpaces
									name={fieldKeys.nominalValue}
									placeholder={disableForLoansSecurityTypes ? '' : '0.00000'}
									disabled={disableForLoansSecurityTypes}
								/>
							</InputWrapper>
						</Cell>
						{showFairMarketValuePerUnitColumn && (
							<Cell align="right">
								<InputWrapper
									onBlur={() => handleBlur(fieldKeys.fairMarketValuePerUnit)}
								>
									<StringInput
										blockRedundantSpaces
										name={fieldKeys.fairMarketValuePerUnit}
										placeholder={disableForLoansSecurityTypes ? '' : '0.00'}
										disabled={disableForLoansSecurityTypes}
									/>
								</InputWrapper>
							</Cell>
						)}

						<Cell align="center">
							<FormCheckbox name={fieldKeys.isSubjectToVesting} />
						</Cell>

						<ButtonsRight gap="sm">
							<CheckedButtonSquare type="button" onClick={onSaveButtonClicked} />
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.SECURITIES_CLOSE_BUTTON}
							>
								<CloseButtonSquare type="button" onClick={onCancelButtonClicked} />
							</UIElementIdentifier>
							{!fieldData.hasTransactions ? (
								<UIElementIdentifier
									identifier={TEST_ELEMENT_IDS.SECURITIES_DELETE_BUTTON}
								>
									<DeleteButtonSquare type="button" onClick={onRemove} />
								</UIElementIdentifier>
							) : null}
						</ButtonsRight>
					</>
				) : (
					<>
						<Cell>
							<Text variant="p-sm">{getSecurityTypeTranslation(fieldData.type)}</Text>
						</Cell>
						<Cell>
							<Text variant="p-sm" elipsis>
								{fieldData.name}
							</Text>
						</Cell>

						<Cell align="right">
							<Text variant="p-sm">{votesPerUnitValue}</Text>
						</Cell>

						<Cell align="right">
							<Text variant="p-sm">{nominalValue}</Text>
						</Cell>
						{showFairMarketValuePerUnitColumn && (
							<Cell align="right">
								<Text variant="p-sm">{fairMarketValuePerUnit}</Text>
							</Cell>
						)}
						<Cell align="center">
							<FormCheckbox disabled={true} name={fieldKeys.isSubjectToVesting} />
						</Cell>
						<Cell align="right">
							{fieldData.locked ? null : (
								<ThreeDotMenuIconButton onClick={onEditButtonClicked} />
							)}
						</Cell>
					</>
				)}
			</Row>
			{showCheckSecurityNameAlert && (
				<AlertModal contentLabel="alert" isOpen className={className}>
					<Alert icon="tick-circle-filled" state="success" size="s">
						<AlertDescription>
							{tCommonUi('securities.alert_description')}
						</AlertDescription>
						<AlertButtonWrapper>
							<ButtonV2
								size="s"
								icon="close-v3"
								buttonTheme="light"
								variant="secondary"
								onClick={onSubmit}
							>
								{tCommonUi('securities.confirm')}
							</ButtonV2>

							<ButtonV2
								size="s"
								icon="close-v3"
								buttonTheme="light"
								variant="ghost"
								onClick={closeAlert}
							>
								{tCommonUi('securities.cancel')}
							</ButtonV2>
						</AlertButtonWrapper>
					</Alert>
				</AlertModal>
			)}
		</RowWrapper>
	);
};

const AlertModal = styled(ReactModal)`
	background-color: ${defaultTheme.v2.color.white[500]};
	border-radius: ${em(8)};
`;

const AlertButtonWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
`;

const Styled = styled(SecurityRowModal)`
	${SecurityTypeSelect} {
		width: 100%;
	}

	${StringInput} {
		width: 100%;
	}

	${InputWrapper} {
		width: 100%;
	}
`;

export { Styled as SecurityRowModal };
